import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Camera, Plus, Search, Upload, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ImageUploader } from '@/components/features/images';
import { PlayerFormDialog } from '@/components/features/players/PlayerFormDialog';
import { PlayerSelectionCard } from '@/components/features/players/PlayerSelectionCard';
import { TeamColorPicker } from '@/components/features/teams/TeamColorPicker';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { listPlayers } from '@/db/players';
import { createTeamPreset } from '@/db/team-presets';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useFormErrorScroll } from '@/hooks/useFormErrorScroll';
import { useTeamAvatar } from '@/hooks/useImages';
import { handleBackgroundError, handleUserFacingError } from '@/lib/error-handler';
import { toastError, toastPromise, toastSuccess } from '@/lib/toast-helpers';
import { parseImageId, parsePlayerId, type ImageId, type Player, type PlayerId } from '@/schemas';
import { TeamPresetSchema } from '@/schemas';
import { useAuthStore } from '@/stores';
import { TEAM_COLOR_PALETTE } from '@/stores/player-utils';

const NewTeamPresetSchema = TeamPresetSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  userId: true, // userId is set from auth store, not form input
  avatarImageId: true, // Omit branded type, use imageId string instead
}).extend({
  playerIds: z.array(z.string()).optional(), // Use string[] in form, convert to PlayerId[] on submit
  imageId: z.string().optional(), // Use string in form, convert to ImageId on submit
});

type NewTeamPresetForm = z.infer<typeof NewTeamPresetSchema>;

export const Route = createFileRoute('/team-presets/new')({
  component: NewTeamPresetPage,
});

function NewTeamPresetPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const userId = useAuthStore((state) => state.userId);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [players, setPlayers] = useState<Player[]>([]);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(true);
  const [showPlayerDialog, setShowPlayerDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingPlayerId, setEditingPlayerId] = useState<PlayerId | undefined>();
  const [avatarUploadOpen, setAvatarUploadOpen] = useState(false);
  const [pendingAvatarImageId, setPendingAvatarImageId] = useState<ImageId | undefined>(undefined);
  const { avatarUrl: teamAvatarUrl } = useTeamAvatar(pendingAvatarImageId);

  useDocumentTitle(`${t('teams.createButton')} - ${t('app.name', 'One Score')}`);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<NewTeamPresetForm>({
    resolver: zodResolver(NewTeamPresetSchema),
    defaultValues: {
      name: undefined,
      nickname: undefined,
      imageId: undefined,
      color: TEAM_COLOR_PALETTE[0], // Preselect first color
      playerIds: undefined,
    },
  });

  const watchedColor = watch('color');
  const watchedName = watch('name');
  const watchedNickname = watch('nickname');
  const watchedPlayerIds = watch('playerIds');

  useEffect(() => {
    void loadPlayers();
  }, []);

  // Scroll to first error field when validation fails
  useFormErrorScroll(errors);

  const loadPlayers = async () => {
    try {
      const playerList = await listPlayers();
      setPlayers(playerList);
    } catch (error: unknown) {
      handleBackgroundError(error, { operation: 'loadPlayers' });
    } finally {
      setIsLoadingPlayers(false);
    }
  };

  const handleColorChange = (color: string) => {
    setValue('color', color, { shouldValidate: true });
  };

  // Filter out selected players from available list
  const availablePlayers = players.filter((p) => !watchedPlayerIds?.includes(String(p.id)));

  const filteredAvailablePlayers = availablePlayers.filter((player) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();
    return (
      player.name.toLowerCase().includes(query) || player.nickname?.toLowerCase().includes(query)
    );
  });

  const selectedPlayers = players.filter((p) => watchedPlayerIds?.includes(String(p.id)));

  const handlePlayerToggle = (playerId: PlayerId) => {
    const currentPlayerIds = watchedPlayerIds ?? [];
    const playerIdString = String(playerId);
    setValue('playerIds', [...currentPlayerIds, playerIdString]);
  };

  const handleRemovePlayer = (playerId: PlayerId) => {
    const currentPlayerIds = watchedPlayerIds ?? [];
    const playerIdString = String(playerId);
    setValue(
      'playerIds',
      currentPlayerIds.filter((id) => id !== playerIdString),
    );
  };

  const handleEditPlayer = (playerId: PlayerId) => {
    setEditingPlayerId(playerId);
    setShowPlayerDialog(true);
  };

  const onSubmit = async (data: NewTeamPresetForm) => {
    setIsCreating(true);
    setError(undefined);

    try {
      // Convert string player IDs to PlayerId if needed
      const playerIds: PlayerId[] | undefined = data.playerIds
        ? data.playerIds.map((id) => (typeof id === 'string' ? parsePlayerId(id) : id))
        : undefined;

      const avatarImageId =
        pendingAvatarImageId ?? (data.imageId ? parseImageId(data.imageId) : undefined);

      await toastPromise(
        createTeamPreset({
          name: data.name,
          nickname: data.nickname,
          avatarImageId,
          color: data.color,
          playerIds,
          userId: userId ?? undefined, // Optional: can create presets without auth
        }),
        {
          loading: t('teams.creating', 'Creating team preset...'),
          success: t('teams.createSuccess', 'Team preset created successfully'),
          error: (err: unknown) =>
            err instanceof Error
              ? err.message
              : t('teams.createError', 'Failed to create team preset'),
        },
      );
      void navigate({ to: '/team-presets' });
    } catch (error: unknown) {
      const errorMessage = handleUserFacingError(
        error,
        t('teams.createError', 'Failed to create team preset'),
        { operation: 'createTeamPreset' },
      );
      setError(errorMessage);
      toastError(errorMessage);
      setIsCreating(false);
    }
  };

  return (
    <div className="w-full max-w-6xl space-y-6 px-4 mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t('newTeamPreset', 'New Team Preset')}
          </h1>
          <p className="text-muted-foreground">
            {t('teams.description', 'Create a reusable team configuration')}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => void navigate({ to: '/team-presets' })}
          disabled={isCreating}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('back', 'Back')}
        </Button>
      </div>

      <form onSubmit={(event) => void handleSubmit(onSubmit)(event)} className="space-y-6">
        <Card className="p-6">
          <div className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">
                  {t('name', 'Name')}{' '}
                  <span className="text-muted-foreground">({t('optional', 'optional')})</span>
                </Label>
                <Input
                  id="name"
                  {...register('name')}
                  placeholder={t('namePlaceholder', 'Enter preset name')}
                  disabled={isCreating}
                  maxLength={100}
                  className={errors.name ? 'border-destructive' : ''}
                />
                {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
                {100 - (watchedName?.length ?? 0) < 20 && (
                  <p className="text-xs text-muted-foreground">
                    {t('form.charactersRemaining', { count: 100 - (watchedName?.length ?? 0) })}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="nickname">
                  {t('nickname', 'Nickname')}{' '}
                  <span className="text-muted-foreground">({t('optional', 'optional')})</span>
                </Label>
                <Input
                  id="nickname"
                  {...register('nickname')}
                  placeholder={t('nicknamePlaceholder', 'Enter preset nickname')}
                  disabled={isCreating}
                  maxLength={50}
                  className={errors.nickname ? 'border-destructive' : ''}
                />
                {errors.nickname && (
                  <p className="text-sm text-destructive">{errors.nickname.message}</p>
                )}
                {50 - (watchedNickname?.length ?? 0) < 20 && (
                  <p className="text-xs text-muted-foreground">
                    {t('form.charactersRemaining', { count: 50 - (watchedNickname?.length ?? 0) })}
                  </p>
                )}
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="color">{t('color', 'Color')}</Label>
                <TeamColorPicker
                  value={watchedColor}
                  onChange={handleColorChange}
                  disabled={isCreating}
                />
                {errors.color && <p className="text-sm text-destructive">{errors.color.message}</p>}
              </div>

              {/* Avatar Upload */}
              <div className="flex flex-col items-center gap-2">
                <Label className="sr-only md:not-sr-only">{t('teams.form.avatar', 'Avatar')}</Label>
                {(() => {
                  const hasAvatar = !!teamAvatarUrl;
                  let tooltipMessage: string;
                  if (isCreating) {
                    tooltipMessage = t(
                      'teams.form.avatarDisabledSubmitting',
                      'Please wait while the form is being submitted',
                    );
                  } else if (hasAvatar) {
                    tooltipMessage = t('teams.form.changeAvatar', 'Click to change avatar');
                  } else {
                    tooltipMessage = t('teams.form.uploadAvatar', 'Click to upload avatar');
                  }

                  return (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setAvatarUploadOpen(true)}
                              disabled={isCreating}
                              className="relative h-24 w-24 rounded-full border-2 p-0 transition-all hover:border-primary hover:shadow-md disabled:opacity-50"
                            >
                              {hasAvatar ? (
                                <>
                                  <img
                                    src={teamAvatarUrl}
                                    alt={t('teams.form.avatar', 'Avatar')}
                                    className="h-full w-full rounded-full object-cover"
                                  />
                                  {/* Overlay on hover */}
                                  <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity hover:opacity-100">
                                    <Upload className="h-6 w-6 text-white" />
                                  </div>
                                </>
                              ) : (
                                <div className="flex h-full w-full flex-col items-center justify-center gap-1 rounded-full bg-muted">
                                  <Camera className="h-6 w-6 text-muted-foreground" />
                                  <span className="text-xs text-muted-foreground">
                                    {t('teams.form.uploadAvatar', 'Upload')}
                                  </span>
                                </div>
                              )}
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{tooltipMessage}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })()}
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="space-y-4">
            <Label>
              {t('players', 'Players')}{' '}
              <span className="text-muted-foreground">({t('optional', 'optional')})</span>
            </Label>
            {isLoadingPlayers ? (
              <div className="flex min-h-[400px] items-center justify-center">
                <p className="text-muted-foreground">{t('loading', 'Loading...')}</p>
              </div>
            ) : (
              <div className="grid gap-6 lg:grid-cols-2">
                {/* Available Players */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">
                      {t('sessions:create.players.available', 'Available Players')}
                    </h3>
                    {availablePlayers.length >= 6 && (
                      <div className="relative">
                        <Label htmlFor="player-search" className="sr-only">
                          {t('sessions:create.players.search', 'Search players...')}
                        </Label>
                        <Search
                          className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                          aria-hidden="true"
                        />
                        <Input
                          id="player-search"
                          placeholder={t('sessions:create.players.search', 'Search players...')}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="h-8 w-48 pl-9"
                          disabled={isCreating}
                          aria-label={t('sessions:create.players.search', 'Search players...')}
                        />
                      </div>
                    )}
                  </div>

                  {availablePlayers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center space-y-4 rounded-lg border-2 border-dashed p-8">
                      <UserPlus className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
                      <p className="text-sm text-muted-foreground">
                        {t('sessions:create.players.noPlayers', 'No players available')}
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {filteredAvailablePlayers.map((player) => (
                        <PlayerSelectionCard
                          key={player.id}
                          player={player}
                          isSelected={false}
                          disabled={isCreating}
                          onToggle={handlePlayerToggle}
                          onEdit={handleEditPlayer}
                        />
                      ))}
                    </div>
                  )}

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setEditingPlayerId(undefined);
                      setShowPlayerDialog(true);
                    }}
                    disabled={isCreating}
                    className="w-full"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t('players.addPlayer')}
                  </Button>
                </div>

                {/* Selected Players */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">
                    {t('sessions:create.players.selected', 'Selected Players')} (
                    {selectedPlayers.length})
                  </h3>

                  {selectedPlayers.length === 0 ? (
                    <div className="flex min-h-[200px] flex-col items-center justify-center space-y-2 rounded-lg border-2 border-dashed p-8">
                      <p className="text-sm text-muted-foreground">
                        {t('sessions:create.players.minimum', 'Select players to add to preset')}
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {selectedPlayers.map((player) => (
                        <PlayerSelectionCard
                          key={player.id}
                          player={player}
                          isSelected={true}
                          disabled={isCreating}
                          onToggle={handleRemovePlayer}
                          onEdit={handleEditPlayer}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            {errors.playerIds && (
              <p className="text-sm text-destructive">{errors.playerIds.message}</p>
            )}
          </div>
        </Card>

        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={isCreating} className="min-w-[120px]">
            {isCreating ? t('creating', 'Creating...') : t('create', 'Create')}
          </Button>
        </div>
      </form>

      <PlayerFormDialog
        open={showPlayerDialog}
        onOpenChange={(open) => {
          setShowPlayerDialog(open);
          if (!open) {
            setEditingPlayerId(undefined);
          }
        }}
        player={editingPlayerId ? players.find((p) => p.id === editingPlayerId) : undefined}
        onSuccess={(playerId) => {
          void loadPlayers();
          // Automatically add newly created player to selected players
          if (playerId) {
            const currentPlayerIds = watchedPlayerIds ?? [];
            const playerIdString = String(playerId);
            if (!currentPlayerIds.includes(playerIdString)) {
              setValue('playerIds', [...currentPlayerIds, playerIdString]);
            }
          }
        }}
      />
      <ImageUploader
        open={avatarUploadOpen}
        onOpenChange={setAvatarUploadOpen}
        entityType="gallery"
        entityId=""
        isPrimary={true}
        maxFiles={1}
        onSuccess={(imageId) => {
          setPendingAvatarImageId(imageId);
          toastSuccess(t('teams.form.avatarUploaded', 'Avatar uploaded successfully'));
          setAvatarUploadOpen(false);
        }}
      />
    </div>
  );
}
